module.exports = {
    slug: 'facture-avoir-comment-etablir',
    title: 'Facture d\'avoir : définition, mentions et exemple chiffré',
    h1: 'Facture d\'avoir : quand l\'émettre, comment l\'établir (avec exemple)',
    description: 'Qu\'est-ce qu\'une facture d\'avoir ? Quand l\'émettre, mentions obligatoires, numérotation, TVA, exemple chiffré et avoir en facturation électronique.',
    category: 'Facturation',
    published: '2026-10-19',
    updated: '2026-11-04',
    lead: 'Une facture émise ne se modifie pas. Si elle comporte une erreur, si le client retourne la marchandise ou si vous accordez une remise après coup, la correction passe par une **facture d\'avoir** (ou simplement un **avoir**) ou par une **facture rectificative**. Le bon document dépend d\'un point : la facture a-t-elle déjà été payée ?',
    sections: [
        {
            id: 'quel-document',
            h2: 'Avoir ou facture rectificative ? Tout dépend du paiement',
            blocks: [
                {table: {
                    head: ['Situation de la facture', 'Ce que vous faites'],
                    rows: [
                        ['Pas encore émise', 'Vous la corrigez ou la supprimez'],
                        ['Émise mais pas encore payée', 'Vous établissez une facture rectificative qui indique qu\'elle « annule et remplace » la facture d\'origine, avec un nouveau numéro'],
                        ['Déjà payée', 'Vous émettez un avoir, document distinct avec son propre numéro, dans la même séquence de numérotation']
                    ]
                }},
                {p: 'Ces règles figurent sur service-public.fr. L\'idée est de ne jamais effacer une facture émise : on conserve la trace de ce qui a été facturé, puis on la corrige par un document daté.'}
            ]
        },
        {
            id: 'cas',
            h2: 'Les cas où l\'on émet un avoir',
            blocks: [
                {ul: [
                    '**Erreur** de prix, de quantité ou de taux de TVA constatée après paiement.',
                    '**Retour** de marchandise ou annulation partielle ou totale de la prestation.',
                    '**Remise** ou geste commercial accordé après la facturation.',
                    '**Restitution d\'un acompte** si le client annule.'
                ]}
            ]
        },
        {
            id: 'contenu',
            h2: 'Ce que doit contenir un avoir',
            blocks: [
                {p: 'L\'avoir se présente comme une facture, avec ses mentions habituelles et ses montants **en négatif**. Il porte :'},
                {ul: [
                    'son **propre numéro**, dans la séquence chronologique et continue de vos factures ;',
                    'la **date d\'émission** ;',
                    'la **référence de la facture d\'origine** (numéro et date) ;',
                    'le **motif** de l\'avoir ;',
                    'les montants HT, TVA et TTC **minorés**, avec les taux de TVA de la facture d\'origine.'
                ]},
                {p: 'Le [guide des mentions obligatoires](/blog/mentions-obligatoires-facture-2026) s\'applique, avec la référence à la facture d\'origine en plus.'}
            ]
        },
        {
            id: 'partiel-total',
            h2: 'Avoir total ou avoir partiel',
            blocks: [
                {p: 'Un **avoir total** annule la facture : le client ne doit plus rien. Un **avoir partiel** n\'en corrige qu\'une partie, par exemple une ligne en trop ou une remise. Dans les deux cas, le solde à régler se calcule sur la facture d\'origine moins les avoirs.'},
                {p: 'Si le client a déjà payé plus que le nouveau montant, l\'avoir peut être **remboursé** ou **imputé** sur une prochaine facture.'}
            ]
        },
        {
            id: 'exemple',
            h2: 'Exemple de facture d\'avoir partiel',
            blocks: [
                {p: 'Une facture de 1 000,00 € HT a été payée. Le client obtient ensuite une remise de 100,00 € HT : vous émettez un **avoir partiel**.'},
                {table: {
                    head: ['Document', 'HT', 'TVA à 20 %', 'TTC'],
                    rows: [
                        ['Facture d\'origine (payée)', '1 000,00 €', '200,00 €', '1 200,00 €'],
                        ['Avoir partiel (remise accordée après coup)', '− 100,00 €', '− 20,00 €', '− 120,00 €'],
                        ['Montant net de l\'opération', '900,00 €', '180,00 €', '1 080,00 €']
                    ]
                }},
                {p: 'Le client a déjà réglé 1 200,00 € alors que l\'opération vaut désormais 1 080,00 € : vous lui devez 120,00 €. L\'avoir peut être **remboursé** ou **imputé** sur sa prochaine facture.'}
            ]
        },
        {
            id: 'electronique',
            h2: 'L\'avoir et la facturation électronique',
            blocks: [
                {p: 'L\'avoir est une facture au sens de la réforme : il suit le même circuit que les autres factures et est transmis via la plateforme agréée. Dans un fichier [Factur-X](/blog/factur-x-format-facture-electronique), il est identifié comme une **note de crédit** (type 381). Voir l\'[exemple de facture Factur-X commenté](/blog/exemple-facture-factur-x-xml).'},
                {p: 'Dans notre [logiciel de facturation](/gestionnaire-citron/devis-factures), un avoir a son propre numéro et son propre statut, rattaché au dossier de la facture qu\'il corrige.'}
            ]
        }
    ],
    faq: [
        {q: 'Qu\'est-ce qu\'une facture d\'avoir ?', a: 'C\'est un document qui annule ou diminue une facture déjà émise et payée : erreur, retour de marchandise, remise accordée après coup. Il a son propre numéro et se rattache à la facture d\'origine.'},
        {q: 'Facture d\'avoir ou avoir : y a-t-il une différence ?', a: 'Non, « facture d\'avoir » et « avoir » désignent le même document. On parle aussi de note de crédit, nom que porte l\'avoir dans un fichier Factur-X (type 381).'},
        {q: 'Peut-on modifier une facture déjà envoyée ?', a: 'Non. Avant paiement, on émet une facture rectificative qui annule et remplace la première. Après paiement, on émet un avoir.'},
        {q: 'L\'avoir doit-il avoir un numéro ?', a: 'Oui. C\'est un document distinct avec son propre numéro, dans la même séquence chronologique et continue que les factures.'},
        {q: 'Que devient la TVA sur un avoir ?', a: 'L\'avoir minore la base et la TVA de la facture d\'origine. La TVA correspondante se régularise dans votre déclaration : voyez votre expert-comptable pour le traitement exact.'},
        {q: 'Un avoir est-il concerné par la facturation électronique ?', a: 'Oui. Il se transmet comme une facture, via une plateforme agréée.'}
    ],
    sources: [
        {label: 'service-public.fr : tout savoir sur la facturation (avoirs et factures rectificatives)', url: 'https://entreprendre.service-public.gouv.fr/vosdroits/F23208'},
        {label: 'economie.gouv.fr : mentions obligatoires d\'une facture', url: 'https://www.economie.gouv.fr/entreprises/gerer-son-entreprise-au-quotidien/gerer-sa-comptabilite-et-ses-demarches/mentions-obligatoires-dune-facture-tout-savoir'},
        {label: 'BOFiP-Impôts : rectification de la facture initiale, facture de remplacement ou note d\'avoir (BOI-TVA-DED-40-10-20)', url: 'https://bofip.impots.gouv.fr/doctrine/BOI-TVA-DED-40-10-20'},
        {label: 'impots.gouv.fr : comment traiter une facture d\'avoir sur ma déclaration de TVA ?', url: 'https://www.impots.gouv.fr/professionnel/questions/comment-traiter-une-facture-davoir-sur-ma-declaration-de-tva'}
    ],
    related: ['facture-acompte-facture-finale', 'factur-x-format-facture-electronique', 'mentions-obligatoires-facture-2026']
};
